import { useEffect, ChangeEvent } from "react";

import { css } from "@compiled/react";
import { useIntl } from "react-intl";

import { Field } from "@atlaskit/form";
import SuccessIcon from "@atlaskit/icon/core/status-success";
import StatusWorkflowDangerIcon from "@atlaskit/icon-lab/core/status-workflow-danger";
import { media } from "@atlaskit/primitives";
import TextField from "@atlaskit/textfield";

import { useAccountCreationContext } from "@/components/signup/account-creation";
import { emailIsValid } from "@/components/signup/utils";
import { wacToken } from "@/libraries/design/foundations";

export const InlineInput = ({
    email,
    setEmail,
    isValid,
    setValid,
    errorMessageText
}: {
    email: string;
    setEmail: React.Dispatch<React.SetStateAction<string>>;
    isValid: boolean | undefined;
    setValid: React.Dispatch<React.SetStateAction<boolean | undefined>>;
    errorMessageText: string;
}) => {
    const {
        isReadyForInvalid,
        setIsEmailValid,
        setIsReadyForInvalid,
        setTypeAheadEmail
    } = useAccountCreationContext();
    const intl = useIntl();

    const placeholderText = intl.formatMessage({
        id: "BxpUiSignup.SignupForm.emailPlaceholder",
        defaultMessage: "you@company.com"
    });

    const validMessage = intl.formatMessage({
        id: "gwp.accountSignin.validEmailIcon",
        defaultMessage: "Email is valid"
    });

    useEffect(() => {
        const valid = emailIsValid(email);

        if (!valid && !isReadyForInvalid) {
            setValid(undefined);
            return;
        }

        setValid(valid);
        setIsEmailValid(valid);
    }, [email, setIsEmailValid, isReadyForInvalid]);

    return (
        <div
            css={[
                containerCss,
                isValid === false && inlineInputInvalidCss,
                isValid === true && inlineInputValidCss
            ]}
        >
            <Field aria-required={true} isRequired label="" name="email">
                {({ fieldProps }) => (
                    <>
                        <div css={inlineInputCss}>
                            <TextField
                                {...fieldProps}
                                autoComplete="email"
                                id="inline_email_embedded_signup_input"
                                aria-label={placeholderText}
                                onBlur={() => setIsReadyForInvalid(true)}
                                onChange={(
                                    e: ChangeEvent<HTMLInputElement>
                                ) => {
                                    setEmail(e.target.value);
                                    setTypeAheadEmail(e.target.value);
                                    fieldProps.onChange(e);
                                }}
                                placeholder={placeholderText}
                                testId="email_field"
                                type="email"
                                value={email}
                            />
                            {isValid === false ? (
                                <div css={[iconCss, invalidIconCss]}>
                                    <StatusWorkflowDangerIcon
                                        label={errorMessageText}
                                    />
                                </div>
                            ) : isValid === true ? (
                                <div css={[iconCss, validIconCss]}>
                                    <SuccessIcon label={validMessage} />
                                </div>
                            ) : null}
                        </div>
                    </>
                )}
            </Field>
        </div>
    );
};

const containerCss = css({
    height: "48px",
    boxSizing: "border-box",
    width: "100%",
    display: "flex",
    alignItems: "center",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors -- override Field wrapper spacing
    ["> div"]: {
        margin: 0,
        padding: 0,
        height: "100%",
        width: "100%"
    },
    [media.above.sm]: {
        height: "100%"
    },
    [media.below.sm]: {
        backgroundColor: wacToken("color.neutral.light0"),
        border: `1px solid ${wacToken("color.neutral.light400")}`,
        borderRadius: 40
    }
});

const inlineInputCss = css({
    display: "flex",
    alignItems: "center",
    height: "100%",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input:-webkit-autofill:focus"]: {
        ["transition"]: "background-color 5000s ease-in-out 0s"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input"]: {
        height: "100%",
        background: "transparent",
        color: wacToken("color.primary.black"),
        fontFamily: wacToken("font.charlie.text"),
        fontSize: "20px",
        overflow: "hidden",
        padding: "0 12px 0 24px !important",
        textOverflow: "ellipsis",
        fontWeight: 300
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input::placeholder"]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "#7D818A",
        fontSize: "20px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["input:focus"]: {
        background: "transparent !important",
        backgroundColor: "transparent !important"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["> div"]: {
        backgroundColor: "transparent !important",
        border: "none",
        borderRadius: 0,
        boxShadow: "none !important",
        height: "100%"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["span"]: {
        alignSelf: "center"
    }
});

const inlineInputInvalidCss = css({
    [media.below.sm]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        borderColor: "var(--Secondary-Secondary-Red, #AE2A19)",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "inset 0 0 0 1px var(--Secondary-Secondary-Red, #AE2A19)"
    }
});

const inlineInputValidCss = css({
    [media.below.sm]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        borderColor: "var(--Extended-Limes-Lime600, #6A9A23)",
        // eslint-disable-next-line local/ensure-wac-token-usage
        boxShadow: "inset 0 0 0 1px var(--Extended-Limes-Lime600, #6A9A23)"
    }
});

const iconCss = css({
    display: "flex",
    alignSelf: "center",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["svg"]: {
        height: "20px",
        width: "20px"
    },
    [media.below.sm]: {
        paddingInlineEnd: "24px"
    }
});

const invalidIconCss = css({
    // eslint-disable-next-line local/ensure-wac-token-usage
    color: "var(--Secondary-Secondary-Red, #AE2A19)"
});

const validIconCss = css({
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ["svg"]: {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "var(--Extended-Limes-Lime600, #6A9A23)"
    }
});
